import { useCallback, useEffect, useRef, useState } from 'react';
import { ChatMobileView } from '../Chat/chatPresentation';

const OVERLAY_ENTRY = 'iokMobileChatOverlay';
let nextEntry = 0;
const isOverlayEntry = (value: unknown): value is string =>
  typeof value === 'string' && /^chat-\d+-\d+$/.test(value);
const entryFromHistory = (): Entry | null => {
  const state = window.history.state;
  if (!isOverlayEntry(state?.[OVERLAY_ENTRY])) return null;
  const { [OVERLAY_ENTRY]: id, ...base } = state;
  return { id, base, url: window.location.href, closing: false };
};
type Entry = { id: string; url: string; base: Record<string, unknown> | null; closing: boolean; skipBaseOnBack?: boolean };

// This is a shell interaction, not a Router navigation. Keep the Router's
// location (including its key/state/index) unchanged for page effects.
export const useMobileChatOverlay = (enabled: boolean, routeKey: string) => {
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState<ChatMobileView>('conversation');
  const openRef = useRef(false);
  const viewRef = useRef<ChatMobileView>('conversation');
  const entryRef = useRef<Entry | null>(null);
  const pendingForwardRef = useRef<number | null>(null);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const routeKeyRef = useRef(routeKey);
  routeKeyRef.current = routeKey;
  const routeIndexRef = useRef(window.history.state?.idx);
  routeIndexRef.current = window.history.state?.idx;

  const cancelPendingForward = useCallback(() => {
    if (pendingForwardRef.current === null) return;
    window.clearTimeout(pendingForwardRef.current);
    pendingForwardRef.current = null;
  }, []);

  const close = useCallback(() => {
    cancelPendingForward();
    const entry = entryRef.current;
    if (!openRef.current || entry?.closing) return;
    openRef.current = false;
    setIsOpen(false);
    viewRef.current = 'conversation';
    setView('conversation');
    if (entry && window.history.state?.[OVERLAY_ENTRY] === entry.id &&
        window.location.href === entry.url) {
      entry.closing = true;
      window.history.back();
    }
  }, [cancelPendingForward]);

  const open = useCallback(() => {
    if (!enabledRef.current || openRef.current || entryRef.current?.closing) return false;
    const historicalEntry = entryFromHistory();
    const entry: Entry = historicalEntry ?? {
      id: 'chat-' + Date.now() + '-' + (++nextEntry),
      url: window.location.href,
      base: window.history.state,
      closing: false
    };
    if (!historicalEntry) {
      window.history.pushState({ ...entry.base, [OVERLAY_ENTRY]: entry.id }, '', entry.url);
    }
    entryRef.current = entry;
    openRef.current = true;
    setIsOpen(true);
    viewRef.current = 'conversation';
    setView('conversation');
    return true;
  }, []);

  const showConversationList = useCallback(() => {
    viewRef.current = 'conversationList';
    setView('conversationList');
  }, []);

  const showConversation = useCallback(() => {
    viewRef.current = 'conversation';
    setView('conversation');
  }, []);

  useEffect(() => {
    const onPopState = (event: PopStateEvent) => {
      cancelPendingForward();
      const previousIndex = routeIndexRef.current;
      let entry = entryRef.current;
      const historicalEntry = entryFromHistory();
      if (historicalEntry && (!entry || historicalEntry.id !== entry.id)) {
        // Restore a historical overlay after a real navigation/reload rather
        // than leaving a closed duplicate page in the browser history.
        entry = historicalEntry;
        entryRef.current = entry;
        if (event.state?.key !== routeKeyRef.current && enabledRef.current) return;
      }
      if (!entry) return;
      const sameUrl = window.location.href === entry.url;
      if (sameUrl && event.state?.[OVERLAY_ENTRY] === entry.id) {
        // Forward into our still-owned entry restores the overlay only.
        event.stopImmediatePropagation();
        entry.closing = false;
        openRef.current = enabledRef.current;
        setIsOpen(enabledRef.current);
        if (!enabledRef.current) {
          const goingBack = typeof event.state?.idx === 'number' &&
            typeof previousIndex === 'number' && event.state.idx < previousIndex;
          entry.skipBaseOnBack = !goingBack;
          // Skip in the direction of travel. At the forward tail there may be
          // no successor; the next Back then skips the duplicate base instead.
          window.history.go(goingBack ? -1 : 1);
        }
        return;
      }
      const returnedToPage = sameUrl && !event.state?.[OVERLAY_ENTRY] &&
        event.state?.key === entry.base?.key && event.state?.idx === entry.base?.idx;
      if (
        returnedToPage &&
        openRef.current &&
        !entry.closing &&
        viewRef.current === 'conversationList'
      ) {
        // Back exposed the base entry and left our existing overlay marker
        // immediately ahead. Return to that marker so the one-column
        // navigation is resolved without truncating any later forward route.
        event.stopImmediatePropagation();
        viewRef.current = 'conversation';
        setView('conversation');
        // Schedule the traversal after the current popstate has completed;
        // nested history traversal is not processed consistently by browsers.
        const expectedEntryId = entry.id;
        const expectedEntryUrl = entry.url;
        const expectedBaseKey = entry.base?.key;
        const expectedBaseIndex = entry.base?.idx;
        pendingForwardRef.current = window.setTimeout(() => {
          pendingForwardRef.current = null;
          const activeEntry = entryRef.current;
          const stillAtExpectedBase = window.location.href === expectedEntryUrl &&
            !window.history.state?.[OVERLAY_ENTRY] &&
            window.history.state?.key === expectedBaseKey &&
            window.history.state?.idx === expectedBaseIndex;
          if (
            !enabledRef.current ||
            !openRef.current ||
            activeEntry?.id !== expectedEntryId ||
            activeEntry.closing ||
            viewRef.current !== 'conversation' ||
            !stillAtExpectedBase
          ) return;
          window.history.forward();
        }, 0);
        return;
      }
      if (returnedToPage && entry.skipBaseOnBack && !entry.closing &&
          event.state?.key === routeKeyRef.current) {
        event.stopImmediatePropagation();
        entryRef.current = null;
        window.history.back();
        return;
      }
      if (returnedToPage && (openRef.current || entry.closing)) {
        // Consume only our transient Back; let all genuine route POPs through.
        event.stopImmediatePropagation();
        entry.closing = false;
      } else {
        entryRef.current = null;
      }
      openRef.current = false;
      setIsOpen(false);
      viewRef.current = 'conversation';
      setView('conversation');
    };
    window.addEventListener('popstate', onPopState, true);
    return () => {
      cancelPendingForward();
      window.removeEventListener('popstate', onPopState, true);
      const entry = entryRef.current;
      if (entry && window.history.state?.[OVERLAY_ENTRY] === entry.id) {
        window.history.replaceState(entry.base, '', window.location.href);
      }
    };
  }, [cancelPendingForward]);

  useEffect(() => {
    if (!enabled) close();
  }, [close, enabled]);

  const previousRouteKey = useRef<string | null>(null);
  useEffect(() => {
    if (previousRouteKey.current === routeKey) return;
    cancelPendingForward();
    previousRouteKey.current = routeKey;
    const historicalEntry = entryFromHistory();
    if (historicalEntry) {
      entryRef.current = historicalEntry;
      openRef.current = enabled;
      setIsOpen(enabled);
      if (!enabled) {
        historicalEntry.closing = true;
        window.history.back();
      }
      return;
    }
    // Real navigation owns the page, but marked history entries can be
    // recognized later without retaining pages, listeners or per-route stores.
    entryRef.current = null;
    openRef.current = false;
    setIsOpen(false);
    viewRef.current = 'conversation';
    setView('conversation');
  }, [cancelPendingForward, enabled, routeKey]);

  return {
    isOpen,
    view,
    open,
    close,
    showConversation,
    showConversationList
  };
};
